import axios from 'axios';
import {
  isGcsResumableUploadUrl,
  putGcsResumableUploadInChunks,
} from './gcsResumableUpload';
import type { UploadAssetDomain } from '@/lib/uploadAssetDomain';
import {
  isRetryableUploadError,
  refreshUploadSession,
} from './uploadRetry';

export type RunFileUploadParams = {
  file: File;
  uploadUrl: string;
  fileId?: number;
  assetDomain: UploadAssetDomain;
  signal: AbortSignal;
  chunkSizeBytes: number;
  maxAttempts: number;
  minChunkSize: number;
  onProgress: (percent: number) => void;
  onRetryMessage: (message: string) => void;
};

export async function runFileUpload({
  file,
  uploadUrl,
  fileId,
  assetDomain,
  signal,
  chunkSizeBytes,
  maxAttempts,
  minChunkSize,
  onProgress,
  onRetryMessage,
}: RunFileUploadParams): Promise<void> {
  let currentUploadUrl = uploadUrl;
  let resumableOffset = 0;
  const chunkSize = Math.max(minChunkSize, chunkSizeBytes);

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (signal.aborted) {
      throw new DOMException('Upload aborted', 'AbortError');
    }

    try {
      if (isGcsResumableUploadUrl(currentUploadUrl)) {
        await putGcsResumableUploadInChunks(
          currentUploadUrl,
          file,
          signal,
          chunkSize,
          (percent) => {
            resumableOffset = Math.floor((percent / 100) * file.size);
            onProgress(percent);
          },
          resumableOffset,
        );
      } else {
        await axios.put(currentUploadUrl, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          signal,
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        });
      }
      return;
    } catch (error) {
      if (signal.aborted) {
        throw error;
      }

      const canRetry =
        fileId !== undefined &&
        isRetryableUploadError(error) &&
        attempt < maxAttempts;

      if (!canRetry) {
        throw error;
      }

      onRetryMessage('Connection issue. Refreshing upload session...');
      currentUploadUrl = await refreshUploadSession(assetDomain, fileId);
      onRetryMessage(`Retrying upload (attempt ${attempt + 1})...`);
    }
  }
}
